import { activeSet, DAILY_BUDGET, DomainError, liveItems, STAGE_ROUNDS, type Account, type DailyPlan, type StudySet } from './model';

export function taipeiDate(time: number): string {
  return new Date(time + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
export function addDays(date: string, days: number): string {
  return new Date(Date.parse(date + 'T00:00:00Z') + days * 86400000).toISOString().slice(0, 10);
}
export function stageRounds(stage: number): number { return STAGE_ROUNDS[stage] ?? 1; }
export function nextStageDelay(stage: number): number { return stage < 5 ? 1 : Math.max(2, stage - 3); }
export function unitId(set: StudySet, ordinal: number): string { return `${set.setId}:${set.stage}:${ordinal}`; }

export function invalidatePlans(account: Account): void {
  for (const plan of Object.values(account.plans)) for (const unit of plan.units) {
    if (unit.status !== 'pending') continue;
    if (account.completedUnits[unit.reviewUnitId]) { unit.status = 'completed'; continue; }
    const set = account.sets.find(candidate => candidate.setId === unit.setId);
    if (!set || !activeSet(account, set) || set.contentRevision !== unit.contentRevision ||
        set.noSchedule || account.categories.some(cat => cat.categoryId === set.categoryId && cat.noSchedule)) unit.status = 'invalid';
  }
}

export function getOrCreatePlan(account: Account, now: number): DailyPlan {
  const studyDate = taipeiDate(now);
  invalidatePlans(account);
  if (account.plans[studyDate]) return account.plans[studyDate];
  const plan: DailyPlan = { uid: account.uid, studyDate, budget: DAILY_BUDGET, units: [] };
  const candidates = account.sets.filter(set => activeSet(account, set) && liveItems(set).length &&
    !set.noSchedule && !account.categories.some(cat => cat.categoryId === set.categoryId && cat.noSchedule) &&
    set.createdDate < studyDate && set.dueDate <= studyDate && set.remaining > 0)
    .sort((a, b) => a.lastAssignedDate.localeCompare(b.lastAssignedDate) || a.dueDate.localeCompare(b.dueDate) || a.setId.localeCompare(b.setId));
  let assigned = 0;
  const maxRounds = Math.max(0, ...candidates.map(set => set.remaining));
  for (let pass = 0; pass < maxRounds; pass++) {
    for (const set of candidates) {
      if (pass >= set.remaining) continue;
      const items = liveItems(set);
      const oversizedFirst = plan.units.length === 0 && pass === 0 && set === candidates[0] && items.length > DAILY_BUDGET;
      if (assigned + items.length > DAILY_BUDGET && !oversizedFirst) continue;
      const ordinal = stageRounds(set.stage) - set.remaining + pass;
      const reviewUnitId = unitId(set, ordinal);
      if (account.completedUnits[reviewUnitId]) throw new DomainError('conflict', '排程輪次已完成，請更新資料');
      plan.units.push({ reviewUnitId, setId: set.setId, kind: set.kind, wordEpoch: set.wordEpoch,
        contentRevision: set.contentRevision, itemIds: items.map(item => item.itemId), itemCount: items.length,
        stage: set.stage, ordinal, cooldownUntil: set.cooldownUntil, status: 'pending' });
      assigned += items.length;
      set.lastAssignedDate = studyDate;
      if (oversizedFirst) { account.plans[studyDate] = plan; return plan; }
    }
  }
  account.plans[studyDate] = plan;
  return plan;
}
